import Role, { LiveChatRole } from "constants/role";
import { IAgent } from "store/entities/agents";
import { ILiveChatAgent } from "store/views/agents";

/**
 * Check if agent matches the query
 *
 * @example agents.filter(filterAgent('test agent'))
 */
export function filterAgent(query: string) {
  const safeQuery = (query || "").toLocaleLowerCase();

  return (agent: IAgent | ILiveChatAgent): boolean => {
    const name = agent.name.toLocaleLowerCase();
    const email = agent.email.toLocaleLowerCase();

    return name.indexOf(safeQuery) !== -1 || email.indexOf(safeQuery) !== -1;
  };
}

const isLiveChatAgent = (agent: IAgent | ILiveChatAgent): agent is ILiveChatAgent => {
  return "imported" in agent;
};

const roleRanksMap: { [key in LiveChatRole | Role]: number } = {
  [LiveChatRole.Owner]: 4,
  [Role.Owner]: 3,
  [LiveChatRole.ViceOwner]: 3,
  [LiveChatRole.Admin]: 3,
  [Role.Normal]: 2,
  [LiveChatRole.Normal]: 2,
  [Role.Viewer]: 1,
};

function getAgentRoleRank(agent: IAgent | ILiveChatAgent): number {
  return roleRanksMap[agent.permission] || 0;
}

const liveChatRolesMap: { [key in LiveChatRole]: Role } = {
  [LiveChatRole.Owner]: Role.Owner,
  [LiveChatRole.ViceOwner]: Role.Owner,
  [LiveChatRole.Admin]: Role.Owner,
  [LiveChatRole.Normal]: Role.Normal,
};

export const helpDeskRoleLabels: { [key in Role]: string } = {
  [Role.Owner]: "Admin",
  [Role.Normal]: "Agent",
  [Role.Viewer]: "Viewer",
};

export function mapLiveChatRoleToHelpDeskRole(liveChatRole: LiveChatRole): Role {
  return liveChatRolesMap[liveChatRole] || Role.Normal;
}

/**
 * Sort agents by field
 */
export function sortAgents(
  field: string,
  desc: boolean,
  agentOnTop?: IAgent | ILiveChatAgent
): (a: IAgent | ILiveChatAgent, b: IAgent | ILiveChatAgent) => number {
  const sortOrder = desc ? -1 : 1;
  switch (field) {
    case "name":
      return (a: IAgent | ILiveChatAgent, b: IAgent | ILiveChatAgent): number => {
        if (a.id === agentOnTop?.id) return -1;
        if (b.id === agentOnTop?.id) return 1;

        return sortOrder * a.name.localeCompare(b.name);
      };
    case "role":
      return (a: IAgent | ILiveChatAgent, b: IAgent | ILiveChatAgent): number => {
        const aRank = getAgentRoleRank(a);
        const bRank = getAgentRoleRank(b);

        if (a.id === agentOnTop?.id) return sortOrder;

        return sortOrder * (aRank - bRank);
      };

    case "auto-assignment":
      return (a: IAgent | ILiveChatAgent, b: IAgent | ILiveChatAgent): number => {
        if (a.id === agentOnTop?.id) return sortOrder;

        if (isLiveChatAgent(a) || isLiveChatAgent(b)) {
          return 0;
        }

        const aCanAcceptTickets = agentCanBeAssignedToTickets(a);
        const bCanAcceptTickets = agentCanBeAssignedToTickets(b);

        if (aCanAcceptTickets !== bCanAcceptTickets) {
          return sortOrder * (aCanAcceptTickets ? -1 : 1);
        }

        if (a.autoassignment !== b.autoassignment) {
          return sortOrder * (a.autoassignment ? -1 : 1);
        }

        return 0;
      };

    default:
      return () => 0;
  }
}

export function agentExists(agents: IAgent[], agentId: string): boolean {
  return agents.some(agent => agent.id === agentId);
}

export function agentCanBeAssignedToTickets(agent: IAgent): boolean {
  return agent.permission !== Role.Viewer && agent.status === "active";
}

export function agentAcceptsAutoassignment(agent: IAgent): boolean {
  return agentCanBeAssignedToTickets(agent) && agent.autoassignment;
}

export function agentAcceptsAutoassignmentFromTeam(agent: IAgent, teamId?: string): boolean {
  return !!(
    agentCanBeAssignedToTickets(agent) &&
    agent.autoassignment &&
    teamId &&
    agent.autoassignmentTeamIDs?.includes(teamId)
  );
}
